import { useState, useRef, useEffect, useMemo } from 'react';
import { Eye, EyeOff, Fingerprint, AlertTriangle, RotateCcw } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/components/ui/Toast';
import { signIn, signUp, verifyMFA, completeNewPassword, isAuthenticated, startOAuthFlow, clearAppCaches, forgotPassword, confirmForgotPassword, signOut, signInWithPasskey, isPasskeySupported, isPlatformAuthenticatorAvailable, getBiometricLabel, requestRecovery, verifyRecovery } from '@/lib/auth';
import { hasLocalPasskey, recordSigninMethod } from '@/lib/passkey-device';
import { markDemoSession } from '@/lib/demo';
import { api } from '@/lib/api';
import { Navigate, useNavigate } from 'react-router-dom';

type Mode = 'login' | 'signup' | 'mfa' | 'new-password' | 'forgot' | 'reset' | 'demo-intake' | 'recover-request' | 'recover-verify';

interface DemoIntake {
  email: string;
  profession: 'physician' | 'attorney' | 'cpa' | 'engineer' | 'consultant' | 'realtor' | 'other' | '';
  phone: string;
  useCase: string;
}

const TAGLINES = [
  'Your business. One platform. Zero chaos.',
  'Expenses, payroll prep, compliance — handled.',
  'Audit-ready from day one.',
  'AI-powered insights for smarter decisions.',
  'Built for owner-operated firms — physicians, attorneys, CPAs, and more.',
  'From receipt to tax return, simplified.',
  'Stop juggling 5 tools. Start using one.',
  'One platform for every entity — LLC, PLLC, PA, PC, LLP, sole prop.',
  'Every profession. Every entity. One back-office.',
];

const inputClass = 'w-full px-3 py-2.5 rounded-[var(--radius-md)] border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent';

/**
 * Device-appropriate name for the platform biometric, so the passkey button
 * reads "Windows Hello" on Windows, "Face ID" on iPhone, "Touch ID" on Mac,
 * "your fingerprint" on Android — instead of always saying "Face ID" (which is
 * Apple-only and confused Windows users). Best-effort from the user agent;
 * falls back to the generic "a passkey". (iPad on iPadOS reports as Macintosh,
 * so it lands on "Touch ID" — acceptable.)
 */
export default function Login() {
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaSession, setMfaSession] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  // NEW_PASSWORD_REQUIRED (admin-set temporary password) — challenge session
  // from signIn; separate from mfaSession because the flows can chain
  // (new password -> MFA_SETUP / SOFTWARE_TOKEN_MFA on this MFA-required pool).
  const [newPasswordSession, setNewPasswordSession] = useState('');
  // Account recovery (lost passkey AND authenticator)
  const [recoverCode, setRecoverCode] = useState('');
  const [recoverPassword, setRecoverPassword] = useState('');
  const [recoverMessage, setRecoverMessage] = useState('');
  const [recoverDone, setRecoverDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [betaAccepted, setBetaAccepted] = useState(() => {
    try { return localStorage.getItem('nexus_beta_accepted') === 'true'; } catch { return false; }
  });
  const [betaCode, setBetaCode] = useState('');
  const [betaError, setBetaError] = useState('');
  const [betaValidating, setBetaValidating] = useState(false);
  const [signupError, setSignupError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [intake, setIntake] = useState<DemoIntake>({ email: '', profession: '', phone: '', useCase: '' });
  const [intakeError, setIntakeError] = useState('');
  const [intakeSubmitted, setIntakeSubmitted] = useState(false);
  const toast = useToast();
  // Piece B (B-2): passkey sign-in. Computed once — feature-detect WebAuthn.
  const [passkeySupported] = useState(() => isPasskeySupported());
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [biometricLabel] = useState(() => getBiometricLabel());
  // Whether THIS device can actually create a local passkey (Windows Hello /
  // Touch ID / Face ID present). Gates only the "we'll set it up here" promise —
  // the cross-device QR fallback still works without a local authenticator.
  const [platformAuthAvailable, setPlatformAuthAvailable] = useState(false);
  // One-method-at-a-time: lead with the passkey and reveal the password only on
  // request, so the screen never shows two competing sign-in buttons at once.
  // Remember the last choice so a returning password-user lands on password.
  const [showPasswordLogin, setShowPasswordLogin] = useState(() => {
    try { return localStorage.getItem('nexus_prefers_password') === 'true'; } catch { return false; }
  });
  // W2-02 Part 1: never promise "Sign in with Windows Hello" when no LOCAL
  // credential exists — that's exactly what sent fresh devices to the surprise
  // QR. The per-user device map (written by registerPasskey) is the source of
  // truth; recomputed as the email changes so user B on user A's device gets
  // the honest password-primary CTA. Empty email = device-level answer.
  const deviceHasPasskey = useMemo(() => hasLocalPasskey(email), [email]);
  // W2-02 Part 3: browsers collapse user-cancel, biometric failure, and
  // timeout into one NotAllowedError. First one per page-load stays SILENT
  // (deliberate dismissals are common); a second consecutive one — or any
  // non-cancel failure — shows the inline fallback banner. Component state
  // only (a one-off failure shouldn't follow the user around).
  const [passkeyFailStreak, setPasskeyFailStreak] = useState(0);
  const [passkeyFallback, setPasskeyFallback] = useState(false);
  // Biometric-primary ONLY when this device actually holds a passkey (the
  // mockup's Step 3). Fresh device -> password-primary with an honestly-labeled
  // cross-device passkey secondary (Step 1).
  const passkeyPrimary = passkeySupported && deviceHasPasskey && !showPasswordLogin;

  const navigate = useNavigate();
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const [taglineIndex, setTaglineIndex] = useState(0);
  const [taglineFade, setTaglineFade] = useState(true);
  const [bootstrapError, setBootstrapError] = useState(() => {
    // Check both storage types (ProtectedRoute writes to localStorage)
    const err = localStorage.getItem('nexus_bootstrap_error') || sessionStorage.getItem('nexus_bootstrap_error');
    if (err) {
      try { localStorage.removeItem('nexus_bootstrap_error'); sessionStorage.removeItem('nexus_bootstrap_error'); } catch { /* */ }
      // A bootstrap failure means the tokens we had were bad (stale, revoked, or
      // sent with wrong pool). Clear them so we don't immediately re-loop through
      // the same failed bootstrap on a retry.
      try { signOut(); } catch { /* */ }
    }
    return err;
  });

  // Detect PWA / standalone / iOS home-screen app context. iOS PWA storage
  // is partitioned from Safari, so the user typically has to sign in once
  // per device install. We surface a soft banner explaining the new
  // HttpOnly cookie path will keep them signed in across long absences.
  const isPwa = typeof window !== 'undefined' && (
    window.matchMedia?.('(display-mode: standalone)')?.matches ||
    // iOS Safari adds this navigator extension when launched from home screen
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );

  // Rotate taglines
  useEffect(() => {
    const interval = setInterval(() => {
      setTaglineFade(false);
      setTimeout(() => {
        setTaglineIndex((i) => (i + 1) % TAGLINES.length);
        setTaglineFade(true);
      }, 300);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Detect browser autofill (Chrome fires animationstart on :-webkit-autofill)
  useEffect(() => {
    const handleAutofill = () => {
      setTimeout(() => {
        if (emailRef.current) setEmail(emailRef.current.value);
        if (passwordRef.current) setPassword(passwordRef.current.value);
      }, 100);
    };
    const emailEl = emailRef.current;
    const pwEl = passwordRef.current;
    emailEl?.addEventListener('animationstart', handleAutofill);
    pwEl?.addEventListener('animationstart', handleAutofill);
    return () => {
      emailEl?.removeEventListener('animationstart', handleAutofill);
      pwEl?.removeEventListener('animationstart', handleAutofill);
    };
  }, []);

  // Probe for a real platform authenticator so the "...we'll set up {biometric}
  // here" promise only shows on devices that can keep it.
  useEffect(() => {
    let alive = true;
    void isPlatformAuthenticatorAvailable().then((ok) => { if (alive) setPlatformAuthAvailable(ok); });
    return () => { alive = false; };
  }, []);

  // If already authenticated, redirect to dashboard
  if (isAuthenticated()) {
    return <Navigate to="/" replace />;
  }

  const handleBetaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBetaError('');
    setBetaValidating(true);
    try {
      const result = await api.validateBetaCode(betaCode.trim());
      if (result?.valid) {
        setBetaAccepted(true);
        try { localStorage.setItem('nexus_beta_accepted', 'true'); } catch { /* */ }
      } else {
        setBetaError('Invalid beta access code. Contact hello@kaspyan.com for access.');
      }
    } catch {
      setBetaError('Could not verify code. Check your connection and try again.');
    } finally {
      setBetaValidating(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    // A fresh sign-in attempt invalidates any stale bootstrap banner.
    setBootstrapError(null);

    // Read from DOM refs as fallback for browser autofill
    const loginEmail = email || emailRef.current?.value || '';
    const loginPassword = password || passwordRef.current?.value || '';

    try {
      const result = await signIn(loginEmail, loginPassword);

      if (result.mfaSetupRequired && result.session) {
        navigate(`/mfa-setup?session=${encodeURIComponent(result.session)}&email=${encodeURIComponent(loginEmail)}`);
        return;
      } else if (result.mfaRequired && result.session) {
        setMfaSession(result.session);
        setEmail(loginEmail); // Ensure email state is set (covers autofill edge case)
        setMode('mfa');
        toast.info('Enter your MFA code');
      } else if (result.newPasswordRequired && result.session) {
        // Temporary-password account (admin-created / admin-reset). The user
        // must choose a permanent password before Cognito will issue tokens.
        setNewPasswordSession(result.session);
        setEmail(loginEmail);
        setNewPassword('');
        setMode('new-password');
        toast.info('Choose a new password to continue');
      } else if (result.success) {
        toast.success('Signed in successfully');
        try { localStorage.setItem('nexus_prefers_password', 'true'); } catch { /* */ }
        // W2-02 Part 2: mark HOW this session began so PasskeyEnrollPrompt can
        // offer one-tap enrollment post-redirect. sessionStorage because both
        // success paths hard-navigate (window.location.href) — it survives the
        // same-tab reload and dies with the tab. Written BEFORE the redirect.
        recordSigninMethod('password');
        markDemoSession(false); // real sign-in: clear any stale demo flag
        await clearAppCaches();
        window.location.href = '/';
      } else {
        toast.error(result.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const validatePassword = (pw: string): string | null => {
    if (pw.length < 12) return 'Password must be at least 12 characters';
    if (!/[A-Z]/.test(pw)) return 'Password needs an uppercase letter';
    if (!/[a-z]/.test(pw)) return 'Password needs a lowercase letter';
    if (!/[0-9]/.test(pw)) return 'Password needs a number';
    if (!/[^A-Za-z0-9]/.test(pw)) return 'Password needs a symbol';
    return null;
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setSignupError('');

    if (!fullName.trim()) { setSignupError('Please enter your full name'); return; }
    if (!email.trim()) { setSignupError('Please enter your email'); return; }
    const pwErr = validatePassword(password);
    if (pwErr) { setSignupError(pwErr); return; }

    setLoading(true);
    try {
      const result = await signUp(email, password, fullName);
      if (result.success) {
        toast.success('Check your email for a verification code');
        navigate('/verify-email?email=' + encodeURIComponent(email));
      } else {
        setSignupError(result.message || 'Could not create account. Please try again.');
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unexpected error during signup';
      setSignupError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleMFA = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await verifyMFA(mfaSession, mfaCode, email);
      if (result.success) {
        toast.success('Signed in successfully');
        // W2-02 Part 2: password+TOTP is still a "password" sign-in for the
        // enroll-prompt gate — written BEFORE the redirect (same as handleLogin).
        recordSigninMethod('password');
        markDemoSession(false); // real sign-in: clear any stale demo flag
        await clearAppCaches();
        window.location.href = '/';
      } else {
        toast.error(result.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // NEW_PASSWORD_REQUIRED — set a permanent password for a temp-password
  // account, then chain into whatever challenge Cognito issues next (this pool
  // requires MFA, so a fresh admin-created account goes straight to MFA setup).
  const handleNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    const pwErr = validatePassword(newPassword);
    if (pwErr) { toast.warning(pwErr); return; }
    setLoading(true);
    try {
      const result = await completeNewPassword(newPasswordSession, email, newPassword);

      if (result.mfaSetupRequired && result.session) {
        // Fresh account with no authenticator yet — continue to TOTP setup.
        setNewPassword('');
        navigate(`/mfa-setup?session=${encodeURIComponent(result.session)}&email=${encodeURIComponent(email)}`);
      } else if (result.mfaRequired && result.session) {
        setNewPassword('');
        setMfaSession(result.session);
        setMode('mfa');
        toast.info('Password updated. Enter your MFA code');
      } else if (result.success) {
        toast.success('Password updated — signed in');
        try { localStorage.setItem('nexus_prefers_password', 'true'); } catch { /* */ }
        recordSigninMethod('password');
        markDemoSession(false);
        await clearAppCaches();
        window.location.href = '/';
      } else if (result.sessionExpired) {
        // The short-lived challenge session lapsed while the user was on this
        // screen — back to sign-in; the temporary password is still valid.
        toast.error(result.message);
        setNewPassword('');
        setMode('login');
      } else {
        toast.error(result.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Account recovery — request a one-time code (lost passkey AND authenticator).
  const handleRecoverRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) { toast.info('Enter your email first.'); return; }
    setLoading(true);
    try {
      const r = await requestRecovery(email.trim());
      setRecoverMessage(r.message);
      setMode('recover-verify'); // always advance (uniform response — no enumeration)
    } finally {
      setLoading(false);
    }
  };

  // Account recovery — verify code + password, which resets MFA on success.
  const handleRecoverVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await verifyRecovery(email.trim(), recoverCode.trim(), recoverPassword);
      if (r.ok) {
        setRecoverDone(true);
        setRecoverMessage(r.message);
        setRecoverCode('');
        setRecoverPassword('');
      } else {
        setRecoverMessage(r.message);
        if (!r.operatorOnly) toast.error(r.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Piece B (B-2): passwordless passkey sign-in (Face ID / Touch ID / Windows
  // Hello). Needs the email (Cognito requires a username to start passkey auth).
  // Because the pool FactorConfiguration is MULTI_FACTOR_WITH_USER_VERIFICATION,
  // the single biometric step satisfies the required-MFA pool — no extra code.
  const handlePasskeySignIn = async () => {
    const loginEmail = (email || emailRef.current?.value || '').trim();
    if (!loginEmail) {
      toast.info(`Enter your email first, then use ${biometricLabel}.`);
      emailRef.current?.focus();
      return;
    }
    setPasskeyLoading(true);
    setBootstrapError(null);
    try {
      const result = await signInWithPasskey(loginEmail);
      if (result.success) {
        toast.success('Signed in with passkey');
        try { localStorage.setItem('nexus_prefers_password', 'false'); } catch { /* */ }
        recordSigninMethod('passkey'); // W2-02: no enroll prompt after a passkey sign-in
        markDemoSession(false);
        await clearAppCaches();
        window.location.href = '/';
      } else if (result.cancelled) {
        // W2-02 Part 3: NotAllowedError is cancel OR biometric-fail OR timeout
        // (browsers deliberately collapse them). First one: likely a dismissal,
        // stay silent. Second consecutive: the user is TRYING and failing —
        // show the fallback banner (never a dead end, never a QR-as-recovery).
        const streak = passkeyFailStreak + 1;
        setPasskeyFailStreak(streak);
        if (streak >= 2) setPasskeyFallback(true);
      } else if (result.noPasskey) {
        // Don't park the user on a passkey screen that can't work — switch to
        // the password form. NO durable nexus_prefers_password write: this is
        // a fallback, not a stated preference.
        toast.info(result.message);
        setShowPasswordLogin(true);
        setTimeout(() => passwordRef.current?.focus(), 0);
      } else if (!result.unsupported) {
        // Real failure (network, unexpected extra challenge) — banner now.
        setPasskeyFallback(true);
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const result = await forgotPassword(email);
      if (result.success) {
        toast.success(result.message);
        setMode('reset');
      } else {
        toast.error(result.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 12) {
      toast.warning('Password must be at least 12 characters');
      return;
    }
    setLoading(true);
    try {
      const result = await confirmForgotPassword(email, resetCode, newPassword);
      if (result.success) {
        toast.success(result.message);
        setResetCode('');
        setNewPassword('');
        setMode('login');
      } else {
        toast.error(result.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Demo mode — full-stack sign-in as the seeded Helix Partners LLC
  // "board" viewer. Drives the real app end-to-end (real DB, real
  // /auth/me, real AI chat), but the role guards prevent demo
  // visitors from generating tangible artifacts:
  //   - no 1099-PDF download, no FIRE export
  //   - no /payroll exports (admin+)
  //   - no /settings / /billing (owner+)
  //   - no /tax or /reports exports (accountant+)
  //   - no /hr, /finance/bills, /projects, /continuity write (bookkeeper+)
  //
  // The viewer still sees Dashboard, Capture, Expenses, Compliance,
  // Activity, Invoices/Contractors read-only, Section 105, Retirement,
  // Documents, ingest/* — the surfaces that showcase the product
  // without exposing anything exportable. Smart Capture lives on
  // /capture and is safe to demo (uploads route to candidate state).
  //
  // NS13 Piece A: the demo session is now SERVER-MINTED. The browser no
  // longer holds the demo password — it POSTs /demo/session, the server
  // completes the (required) MFA challenge from a Secrets-Manager-held TOTP
  // secret, and returns JWTs. This both fixes the demo (the pool is
  // MfaConfiguration=ON) and removes the shared password from the JS bundle.
  // Nightly EventBridge cron resets Helix data at 03:30 UTC so demo
  // mutations disappear overnight; the demo lands as a read-only viewer.
  // "Request a demo" → show the request form. Demos are COMPANY-GUIDED; there
  // is NO public self-serve session (IP protection, 2026-05-31). The public can
  // only leave their info; the team follows up to schedule a guided walkthrough.
  const handleDemoLogin = () => {
    setIntakeError('');
    setIntakeSubmitted(false);
    setMode('demo-intake');
  };

  // Submit the demo request → POST /demo/intake (lead capture only). No instant
  // session is minted. On success, show a confirmation.
  const handleIntakeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIntakeError('');

    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(intake.email.trim());
    if (!emailValid) {
      setIntakeError('Please enter a valid email address.');
      return;
    }
    if (!intake.profession) {
      setIntakeError('Please select what you do.');
      return;
    }

    setLoading(true);
    try {
      await api.submitDemoIntake({
        email: intake.email.trim(),
        profession: intake.profession,
        phone: intake.phone.trim() || undefined,
        useCase: intake.useCase.trim() || undefined,
      });
      setIntakeSubmitted(true);
    } catch (err) {
      setIntakeError(err instanceof Error ? err.message : 'Could not submit your request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { title: 'Expense Capture', desc: 'Snap a receipt, speak it, or add it by hand — three fast ways to capture, with email and text on the way.' },
    { title: 'IRS-Code Tagged', desc: 'Every transaction auto-tagged with IRC sections for audit-ready compliance' },
    { title: 'AI-Powered Insights', desc: 'Ask Nexus anything about your finances with intelligent Claude AI routing' },
    { title: 'Multi-Entity Support', desc: 'Manage multiple businesses (LLC, PLLC, PA, PC, LLP, sole prop) under one account with full data isolation' },
    { title: 'Tax Planning', desc: 'Quarterly estimates, Solo 401(k) optimization, and safe harbor calculations' },
    { title: 'Payroll Ready', desc: 'Compensation staging with FMV validation and CSV export for your processor' },
  ];

  return (
    <div className="min-h-dvh bg-slate-50 dark:bg-slate-900 flex">
      {/* Left panel — promotional (desktop only) */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-[55%] bg-gradient-to-br from-primary-700 via-primary-800 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-primary-400 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-primary-300 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-16 py-12 max-w-2xl">
          <div className="flex items-center gap-3 mb-8">
            <img src="/nexus-logo-mark.svg" alt="Nexus" className="w-10 h-10 brightness-0 invert" />
            <span className="font-headline text-2xl font-semibold text-white">Kaspyan Nexus</span>
          </div>

          <h2 className="font-headline text-3xl xl:text-4xl font-bold text-white leading-tight mb-3">
            Back-Office,<br />Simplified.
          </h2>
          <p
            className={`text-primary-200 text-lg mb-10 h-7 transition-opacity duration-300 ${taglineFade ? 'opacity-100' : 'opacity-0'}`}
          >
            {TAGLINES[taglineIndex]}
          </p>

          <div className="grid gap-4">
            {features.map((f) => (
              <div key={f.title} className="flex gap-3 items-start">
                <div className="mt-1 w-5 h-5 rounded-full bg-primary-500/30 flex items-center justify-center shrink-0">
                  <div className="w-2 h-2 rounded-full bg-primary-300" />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">{f.title}</p>
                  <p className="text-primary-200/70 text-xs mt-0.5">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 pt-6 border-t border-white/10">
            <p className="text-primary-200/50 text-xs">Kaspyan Group LLC</p>
          </div>
        </div>
      </div>

      {/* Right panel — login form or beta gate */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-8">
        <div className="w-full max-w-md">
          {/* Logo — mobile only (desktop shows in left panel) */}
          <div className="text-center mb-8 lg:hidden">
            <img src="/nexus-logo-mark.svg" alt="Nexus" className="w-14 h-14 mx-auto mb-4" />
            <h1 className="font-headline text-2xl font-semibold text-slate-900 dark:text-white">Kaspyan Nexus</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Back-office for solo and small-firm practices</p>
            <p
              className={`text-xs text-primary-600 dark:text-primary-400 mt-2 h-4 transition-opacity duration-300 ${taglineFade ? 'opacity-100' : 'opacity-0'}`}
            >
              {TAGLINES[taglineIndex]}
            </p>
          </div>

          {/* Bootstrap error — shown in ALL modes (beta gate and login) so silent bounces are visible */}
          {bootstrapError && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-[var(--radius-md)]">
              <p className="text-xs font-medium text-red-700 dark:text-red-300">Sign-in failed:</p>
              <p className="text-xs text-red-600 dark:text-red-400 mt-1 break-all">{bootstrapError}</p>
            </div>
          )}

          {/* Beta gate */}
          {!betaAccepted ? (
            <>
              <div className="hidden lg:block mb-8">
                <h1 className="font-headline text-2xl font-semibold text-slate-900 dark:text-white">Early Access</h1>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Nexus is currently in private beta</p>
              </div>

              <Card padding="lg">
                <div className="text-center mb-6">
                  <div className="w-14 h-14 rounded-full bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center mx-auto mb-4">
                    <svg className="w-7 h-7 text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                    </svg>
                  </div>
                  <h2 className="font-headline text-lg font-semibold text-slate-900 dark:text-white">Private Beta</h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                    Kaspyan Nexus is in pre-release beta testing. Enter your beta access code to continue.
                  </p>
                </div>

                <form onSubmit={handleBetaSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="login-beta-code" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Beta Access Code</label>
                    <input
                      id="login-beta-code"
                      type="text"
                      value={betaCode}
                      onChange={(e) => { setBetaCode(e.target.value); setBetaError(''); }}
                      className={`${inputClass} font-mono text-center tracking-wider uppercase`}
                      placeholder="XXXX-XXXX-XXXX"
                      required
                      autoFocus
                    />
                  </div>
                  {betaError && (
                    <p className="text-sm text-red-500 dark:text-red-400 text-center">{betaError}</p>
                  )}
                  <Button size="lg" className="w-full" disabled={betaValidating}>
                    {betaValidating ? 'Verifying…' : 'Unlock Access'}
                  </Button>
                </form>

                <div className="mt-6 pt-4 border-t border-slate-100 dark:border-slate-700">
                  <p className="text-xs text-slate-400 dark:text-slate-500 text-center">
                    Don't have a code? Contact{' '}
                    <a href="mailto:hello@kaspyan.com" className="text-primary-600 dark:text-primary-400 hover:underline">
                      hello@kaspyan.com
                    </a>{' '}
                    to request early access.
                  </p>
                </div>
              </Card>

              <p className="text-xs text-slate-400 dark:text-slate-500 text-center mt-4">
                By accessing the beta, you agree to keep all features and data confidential.
              </p>
            </>
          ) : (
            <>
              {/* Desktop header */}
              <div className="hidden lg:block mb-8">
                <h1 className="font-headline text-2xl font-semibold text-slate-900 dark:text-white">
                  {mode === 'signup' ? 'Get started' : mode === 'forgot' || mode === 'reset' ? 'Reset password' : mode === 'new-password' ? 'Set a new password' : 'Welcome back'}
                </h1>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  {mode === 'signup' ? 'Create your Nexus account' : mode === 'forgot' || mode === 'reset' ? 'We\u2019ll email you a reset code' : mode === 'new-password' ? 'Replace your temporary password' : 'Sign in to your Nexus account'}
                </p>
              </div>

          {isPwa && mode === 'login' && (
            <div className="mb-4 rounded-md bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800/40 px-3 py-2 text-xs text-primary-900 dark:text-primary-200">
              Welcome to Nexus on this device. Sign in once and we&apos;ll keep you signed in even if you don&apos;t open the app for a while.
            </div>
          )}

          <Card padding="lg">
          {/* Mode tabs */}
          {(mode === 'login' || mode === 'signup') && (
            <div className="flex gap-1 mb-6 bg-slate-100 dark:bg-slate-700 rounded-[var(--radius-md)] p-1">
              <button
                onClick={() => setMode('login')}
                className={`flex-1 py-2 rounded-[var(--radius-sm)] text-sm font-medium transition-colors ${mode === 'login' ? 'bg-white dark:bg-slate-600 text-primary-600 shadow-sm' : 'text-slate-500'}`}
              >
                Sign In
              </button>
              <button
                onClick={() => setMode('signup')}
                className={`flex-1 py-2 rounded-[var(--radius-sm)] text-sm font-medium transition-colors ${mode === 'signup' ? 'bg-white dark:bg-slate-600 text-primary-600 shadow-sm' : 'text-slate-500'}`}
              >
                Sign Up
              </button>
            </div>
          )}

          {/* Login form */}
          {mode === 'login' && (
            <form onSubmit={(e) => { e.preventDefault(); if (passkeyPrimary) { void handlePasskeySignIn(); } else { void handleLogin(e); } }} className="space-y-4">
              <div>
                <label htmlFor="login-email" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Email</label>
                <input id="login-email" ref={emailRef} type="email" value={email} onChange={(e) => { setEmail(e.target.value); setBootstrapError(null); }} className={inputClass} required autoFocus autoComplete="email" />
              </div>
              {/* ONE METHOD AT A TIME (W2-02). Biometric-primary ONLY when this
                  device holds a local passkey for the entered user — never
                  promise "Windows Hello" that would fall to the cross-device QR
                  (the W2-02 surprise-QR bug). Fresh device: password-primary
                  with an honestly-labeled cross-device passkey secondary. A
                  verified passkey satisfies the required-MFA pool in ONE step;
                  password + TOTP remain the fallback. The choice is remembered. */}
              {passkeyPrimary ? (
                passkeyFallback ? (
                  <>
                    {/* W2-02 Part 3: graceful biometric-failure fallback. Never a
                        dead end; NO QR offered here (the QR is for borrowing a
                        passkey from another device, not recovery). */}
                    <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 rounded-[var(--radius-md)] flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" aria-hidden />
                      <p className="text-xs text-amber-800 dark:text-amber-200">
                        <span className="font-semibold">{biometricLabel} couldn't verify you.</span>{' '}
                        No problem — sign in another way below. Your account is unchanged.
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="secondary"
                      size="lg"
                      className="w-full gap-2"
                      onClick={handlePasskeySignIn}
                      loading={passkeyLoading}
                    >
                      {!passkeyLoading && <RotateCcw className="w-4 h-4 shrink-0" />}
                      Try {biometricLabel} again
                    </Button>
                    <Button
                      type="button"
                      size="lg"
                      className="w-full"
                      onClick={() => {
                        // One-off failure — switch forms WITHOUT writing the
                        // durable nexus_prefers_password preference.
                        setShowPasswordLogin(true);
                        setPasskeyFallback(false);
                        setPasskeyFailStreak(0);
                        setTimeout(() => passwordRef.current?.focus(), 0);
                      }}
                    >
                      Use password + authenticator code
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      type="button"
                      size="lg"
                      className="w-full gap-2"
                      onClick={handlePasskeySignIn}
                      loading={passkeyLoading}
                    >
                      {!passkeyLoading && <Fingerprint className="w-5 h-5 shrink-0" />}
                      Sign in with {biometricLabel}
                    </Button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowPasswordLogin(true);
                        try { localStorage.setItem('nexus_prefers_password', 'true'); } catch { /* */ }
                        setTimeout(() => passwordRef.current?.focus(), 0);
                      }}
                      className="w-full text-sm text-slate-500 hover:text-primary-600 dark:text-slate-400 dark:hover:text-primary-400 mt-1"
                    >
                      Use a password instead
                    </button>
                    <button type="button" onClick={() => setMode('forgot')} className="w-full text-xs text-slate-400 hover:text-primary-600 dark:hover:text-primary-400">
                      Forgot password?
                    </button>
                    <p className="text-xs text-slate-400 text-center pt-1">
                      No QR — {biometricLabel} is set up on this device.
                    </p>
                  </>
                )
              ) : (
                <>
                  <div>
                    <label htmlFor="login-password" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Password</label>
                    <div className="relative">
                      <input id="login-password" ref={passwordRef} type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => { setPassword(e.target.value); setBootstrapError(null); }} className={`${inputClass} pr-10`} required autoComplete="current-password" />
                      <button type="button" onClick={() => setShowPassword((v) => !v)} aria-label={showPassword ? 'Hide password' : 'Show password'} aria-pressed={showPassword} tabIndex={-1} className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  <Button type="submit" size="lg" className="w-full" loading={loading}>
                    {passkeySupported && !deviceHasPasskey ? 'Continue with password' : 'Sign In'}
                  </Button>
                  {passkeySupported && deviceHasPasskey && (
                    <button
                      type="button"
                      onClick={() => {
                        setShowPasswordLogin(false);
                        try { localStorage.setItem('nexus_prefers_password', 'false'); } catch { /* */ }
                      }}
                      className="w-full text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 flex items-center justify-center gap-1.5"
                    >
                      <Fingerprint className="w-4 h-4 shrink-0" /> Use {biometricLabel} instead
                    </button>
                  )}
                  {passkeySupported && !deviceHasPasskey && (
                    <>
                      {/* W2-02 Part 1: honestly-labeled cross-device option — the
                          QR is now EXPECTED, not a surprise. */}
                      <Button
                        type="button"
                        variant="secondary"
                        size="lg"
                        className="w-full gap-2"
                        onClick={handlePasskeySignIn}
                        loading={passkeyLoading}
                      >
                        {!passkeyLoading && <Fingerprint className="w-4 h-4 shrink-0" />}
                        Sign in with a passkey from another device
                      </Button>
                      <p className="text-xs text-slate-400 text-center">
                        This shows a QR code so you can use a passkey saved on your phone or
                        another device.{platformAuthAvailable && <> After you sign in, we'll offer to set up {biometricLabel} here
                        — then no password and no QR on this device.</>}
                      </p>
                    </>
                  )}
                  <button type="button" onClick={() => setMode('forgot')} className="w-full text-sm text-slate-500 hover:text-primary-600 dark:text-slate-400 dark:hover:text-primary-400 mt-1">
                    Forgot password?
                  </button>
                </>
              )}
            </form>
          )}

          {/* Signup form */}
          {mode === 'signup' && (
            <form onSubmit={handleSignup} className="space-y-4">
              <div>
                <label htmlFor="signup-fullname" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Full Name</label>
                <input id="signup-fullname" type="text" value={fullName} onChange={(e) => { setFullName(e.target.value); setSignupError(''); }} className={inputClass} required autoFocus />
              </div>
              <div>
                <label htmlFor="signup-email" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Email</label>
                <input id="signup-email" type="email" value={email} onChange={(e) => { setEmail(e.target.value); setSignupError(''); }} className={inputClass} required />
              </div>
              <div>
                <label htmlFor="signup-password" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Password</label>
                <div className="relative">
                  <input id="signup-password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => { setPassword(e.target.value); setSignupError(''); }} className={`${inputClass} pr-10`} required minLength={12} autoComplete="new-password" />
                  <button type="button" onClick={() => setShowPassword((v) => !v)} aria-label={showPassword ? 'Hide password' : 'Show password'} aria-pressed={showPassword} tabIndex={-1} className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-xs text-slate-400 mt-1">Min 12 characters, mixed case, number, symbol</p>
              </div>
              {signupError && (
                <div role="alert" className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-[var(--radius-sm)] px-3 py-2">
                  {signupError}
                </div>
              )}
              <Button size="lg" className="w-full" loading={loading}>Create Account</Button>
            </form>
          )}

          {/* MFA form */}
          {mode === 'mfa' && (
            <form onSubmit={handleMFA} className="space-y-4">
              <p className="text-sm text-slate-600 dark:text-slate-400">Enter the 6-digit code from your authenticator app.</p>
              <div>
                <label htmlFor="mfa-code" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">MFA Code</label>
                <input id="mfa-code" type="text" value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} className={`${inputClass} font-mono text-center text-2xl tracking-widest`} maxLength={6} required autoFocus />
              </div>
              <Button size="lg" className="w-full" loading={loading}>Verify</Button>
              <button type="button" onClick={() => { setRecoverMessage(''); setRecoverDone(false); setMode('recover-request'); }} className="w-full text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400">
                Can't use your passkey or authenticator?
              </button>
              <button type="button" onClick={() => setMode('login')} className="w-full text-sm text-slate-500 hover:text-slate-700">
                Back to sign in
              </button>
            </form>
          )}

          {/* Set a new password — NEW_PASSWORD_REQUIRED (temp-password account) */}
          {mode === 'new-password' && (
            <form onSubmit={handleNewPassword} className="space-y-4">
              <h2 className="text-lg font-headline font-semibold text-slate-900 dark:text-white">Set your new password</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                You signed in with a temporary password. Choose a permanent password to continue
                {' '}— you may be asked to set up an authenticator app next.
              </p>
              <div>
                <label htmlFor="new-password" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">New Password</label>
                <div className="relative">
                  <input id="new-password" type={showPassword ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className={`${inputClass} pr-10`} required minLength={12} autoFocus autoComplete="new-password" />
                  <button type="button" onClick={() => setShowPassword((v) => !v)} aria-label={showPassword ? 'Hide password' : 'Show password'} aria-pressed={showPassword} tabIndex={-1} className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-xs text-slate-400 mt-1">Min 12 characters, mixed case, number, symbol</p>
              </div>
              <Button size="lg" className="w-full" loading={loading}>Set password & continue</Button>
              <button type="button" onClick={() => { setNewPassword(''); setMode('login'); }} className="w-full text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300">
                Back to sign in
              </button>
            </form>
          )}

          {/* Account recovery — request a one-time code */}
          {mode === 'recover-request' && (
            <form onSubmit={handleRecoverRequest} className="space-y-4">
              <h2 className="text-lg font-headline font-semibold text-slate-900 dark:text-white">Recover your account</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">Lost both your passkey and your authenticator? Enter your email and we'll send a one-time code. You'll also need your password.</p>
              <div>
                <label htmlFor="recover-request-email" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Email</label>
                <input id="recover-request-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} required autoFocus autoComplete="email" />
              </div>
              <Button size="lg" className="w-full" loading={loading}>Send recovery code</Button>
              <p className="text-xs text-slate-400 text-center">Can't get a code? Email <a href="mailto:support@kaspyan.com" className="text-primary-600 hover:underline">support@kaspyan.com</a> and we'll verify you manually.</p>
              <button type="button" onClick={() => setMode('login')} className="w-full text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300">
                Back to sign in
              </button>
            </form>
          )}

          {/* Account recovery — verify code + password (resets MFA) */}
          {mode === 'recover-verify' && (
            recoverDone ? (
              <div className="space-y-4 text-center">
                <h2 className="text-lg font-headline font-semibold text-slate-900 dark:text-white">You're all set</h2>
                <p className="text-sm text-slate-600 dark:text-slate-400">{recoverMessage}</p>
                <Button size="lg" className="w-full" onClick={() => { setMode('login'); setRecoverDone(false); }}>Back to sign in</Button>
              </div>
            ) : (
              <form onSubmit={handleRecoverVerify} className="space-y-4">
                <h2 className="text-lg font-headline font-semibold text-slate-900 dark:text-white">Enter your code + password</h2>
                {recoverMessage && (
                  <p className="text-sm text-slate-500 dark:text-slate-400">{recoverMessage}</p>
                )}
                <div>
                  <label htmlFor="recover-code" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Recovery code</label>
                  <input id="recover-code" type="text" inputMode="numeric" value={recoverCode} onChange={(e) => setRecoverCode(e.target.value)} className={`${inputClass} font-mono text-center text-xl tracking-widest`} maxLength={6} required autoFocus />
                </div>
                <div>
                  <label htmlFor="recover-password" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Your password</label>
                  <div className="relative">
                    <input id="recover-password" type={showPassword ? 'text' : 'password'} value={recoverPassword} onChange={(e) => setRecoverPassword(e.target.value)} className={`${inputClass} pr-10`} required autoComplete="current-password" />
                    <button type="button" onClick={() => setShowPassword((v) => !v)} aria-label={showPassword ? 'Hide password' : 'Show password'} aria-pressed={showPassword} tabIndex={-1} className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <Button size="lg" className="w-full" loading={loading}>Reset & continue</Button>
                <p className="text-xs text-slate-400 text-center">Code didn't arrive, or this account needs manual recovery? Email <a href="mailto:support@kaspyan.com" className="text-primary-600 hover:underline">support@kaspyan.com</a>.</p>
                <button type="button" onClick={() => setMode('login')} className="w-full text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300">
                  Back to sign in
                </button>
              </form>
            )
          )}

          {/* Forgot password — enter email */}
          {mode === 'forgot' && (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <h2 className="text-lg font-headline font-semibold text-slate-900 dark:text-white">Reset Password</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">Enter your email and we'll send a verification code to reset your password.</p>
              {/* FORCE_CHANGE_PASSWORD accounts never receive a reset code
                  (Cognito reports success without sending one, by design).
                  Point temp-password users back to sign-in instead. */}
              <div className="p-3 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800/40 rounded-[var(--radius-md)]">
                <p className="text-xs text-primary-900 dark:text-primary-200">
                  <span className="font-semibold">Were you given a temporary password?</span>{' '}
                  Don't reset — a code won't be sent. Go back and sign in with the temporary
                  password; you'll be asked to choose a new one.
                </p>
              </div>
              <div>
                <label htmlFor="forgot-email" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Email</label>
                <input id="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} required autoFocus autoComplete="email" />
              </div>
              <Button size="lg" className="w-full" loading={loading}>Send Reset Code</Button>
              <button type="button" onClick={() => setMode('login')} className="w-full text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300">
                Back to sign in
              </button>
            </form>
          )}

          {/* Reset password — enter code + new password */}
          {mode === 'reset' && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <h2 className="text-lg font-headline font-semibold text-slate-900 dark:text-white">Enter New Password</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">Check your email for the 6-digit code, then set your new password.</p>
              <div>
                <label htmlFor="reset-code" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Verification Code</label>
                <input id="reset-code" type="text" value={resetCode} onChange={(e) => setResetCode(e.target.value)} className={`${inputClass} font-mono text-center text-xl tracking-widest`} maxLength={6} required autoFocus />
              </div>
              <div>
                <label htmlFor="reset-newpassword" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">New Password</label>
                <div className="relative">
                  <input id="reset-newpassword" type={showPassword ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className={`${inputClass} pr-10`} required minLength={12} autoComplete="new-password" />
                  <button type="button" onClick={() => setShowPassword((v) => !v)} aria-label={showPassword ? 'Hide password' : 'Show password'} aria-pressed={showPassword} tabIndex={-1} className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-xs text-slate-400 mt-1">Min 12 characters, mixed case, number, symbol</p>
              </div>
              <Button size="lg" className="w-full" loading={loading}>Reset Password</Button>
              <div className="flex justify-between">
                <button type="button" onClick={() => setMode('forgot')} className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300">
                  Resend code
                </button>
                <button type="button" onClick={() => setMode('login')} className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300">
                  Back to sign in
                </button>
              </div>
            </form>
          )}

          {/* Demo request — company-guided. Lead capture only; no self-serve session. */}
          {mode === 'demo-intake' && intakeSubmitted && (
            <div className="text-center space-y-3 py-6">
              <div className="w-14 h-14 rounded-full bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center mx-auto">
                <svg className="w-7 h-7 text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
              </div>
              <h2 className="font-headline text-xl font-semibold text-slate-900 dark:text-white">Request received</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">Thanks — our team will reach out shortly to schedule a guided walkthrough of Kaspyan Nexus.</p>
              <button type="button" onClick={() => { setMode('login'); setIntakeSubmitted(false); }} className="text-sm text-primary-600 dark:text-primary-400 hover:underline">
                Back to sign in
              </button>
            </div>
          )}
          {mode === 'demo-intake' && !intakeSubmitted && (
            <form onSubmit={handleIntakeSubmit} className="space-y-4">
              <div className="text-center">
                <h2 className="font-headline text-xl font-semibold text-slate-900 dark:text-white">Request a demo</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Tell us a bit about you and we'll set up a personalized, guided walkthrough.</p>
              </div>

              <div>
                <label htmlFor="waitlist-email" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Email *</label>
                <input
                  id="waitlist-email"
                  type="email"
                  value={intake.email}
                  onChange={(e) => { setIntake((p) => ({ ...p, email: e.target.value })); setIntakeError(''); }}
                  className={inputClass}
                  required
                  autoFocus
                  autoComplete="email"
                  placeholder="you@example.com"
                />
              </div>

              <div>
                <label htmlFor="waitlist-profession" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">What do you do? *</label>
                <select
                  id="waitlist-profession"
                  value={intake.profession}
                  onChange={(e) => { setIntake((p) => ({ ...p, profession: e.target.value as DemoIntake['profession'] })); setIntakeError(''); }}
                  className={inputClass}
                  required
                >
                  <option value="">Select...</option>
                  <option value="physician">Physician</option>
                  <option value="attorney">Attorney</option>
                  <option value="cpa">CPA / Accountant</option>
                  <option value="engineer">Engineer / Architect</option>
                  <option value="consultant">Consultant</option>
                  <option value="realtor">Realtor / Real Estate</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="waitlist-phone" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Phone <span className="text-slate-400 font-normal">(optional)</span></label>
                <input
                  id="waitlist-phone"
                  type="tel"
                  value={intake.phone}
                  onChange={(e) => setIntake((p) => ({ ...p, phone: e.target.value }))}
                  className={inputClass}
                  autoComplete="tel"
                  placeholder="+1 555 555 5555"
                />
              </div>

              <div>
                <label htmlFor="waitlist-message" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">What brings you here? <span className="text-slate-400 font-normal">(optional)</span></label>
                <textarea
                  id="waitlist-message"
                  value={intake.useCase}
                  onChange={(e) => setIntake((p) => ({ ...p, useCase: e.target.value }))}
                  className={`${inputClass} resize-none`}
                  rows={2}
                  maxLength={500}
                  placeholder="One sentence is plenty."
                />
              </div>

              {intakeError && (
                <div role="alert" className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-[var(--radius-sm)] px-3 py-2">
                  {intakeError}
                </div>
              )}

              <Button size="lg" className="w-full" loading={loading}>Request demo</Button>

              <div className="flex flex-col items-center gap-1 pt-1">
                <button type="button" onClick={() => setMode('login')} className="text-xs text-slate-400 hover:text-slate-500">
                  Back to sign in
                </button>
              </div>
              <p className="text-xs text-center text-slate-400 pt-2">
                We'll only email you about Nexus. Unsubscribe anytime.
              </p>
            </form>
          )}
        </Card>

        {/* OAuth SSO buttons — hidden in demo-intake mode to keep the form focused,
            and hidden in new-password mode because Google/Microsoft do a hard
            window.location.href redirect that would destroy the in-memory
            NEW_PASSWORD_REQUIRED challenge session before it completes, dead-ending
            the exact temp-password-cutover flow this screen exists for. */}
        {mode !== 'demo-intake' && mode !== 'new-password' && (<>
        <div className="mt-4 space-y-2">
          <div className="relative">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200 dark:border-slate-700" /></div>
            <div className="relative flex justify-center text-xs"><span className="px-2 bg-slate-50 dark:bg-slate-900 text-slate-400">or continue with</span></div>
          </div>

          <div className="grid grid-cols-2 gap-2 mt-3">
            <button
              onClick={async () => { await startOAuthFlow('Google'); }}
              className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-[var(--radius-md)] border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-sm font-medium text-slate-700 dark:text-slate-300 transition-colors"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Google
            </button>
            <button
              onClick={async () => { await startOAuthFlow('Microsoft'); }}
              className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-[var(--radius-md)] border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-sm font-medium text-slate-700 dark:text-slate-300 transition-colors"
            >
              <svg className="w-4 h-4" viewBox="0 0 23 23">
                <rect fill="#F25022" x="1" y="1" width="10" height="10"/>
                <rect fill="#00A4EF" x="1" y="12" width="10" height="10"/>
                <rect fill="#7FBA00" x="12" y="1" width="10" height="10"/>
                <rect fill="#FFB900" x="12" y="12" width="10" height="10"/>
              </svg>
              Microsoft
            </button>
          </div>
        </div>

          {/* Sign up / sign in toggle + Demo mode */}
          <div className="mt-4 text-center space-y-2">
            {mode === 'signup' ? (
              <p className="text-sm text-slate-500">Already have an account? <button type="button" onClick={() => { setMode('login'); setSignupError(''); }} className="text-primary-600 hover:text-primary-700 font-medium">Sign In</button></p>
            ) : (
              <p className="text-sm text-slate-500">Don't have an account? <button type="button" onClick={() => setMode('signup')} className="text-primary-600 hover:text-primary-700 font-medium">Sign Up</button></p>
            )}
            <button type="button" onClick={handleDemoLogin} disabled={loading} className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium disabled:opacity-50">
              Request a demo
            </button>
          </div>
            </>
          )}
        </>)}
        </div>
      </div>
    </div>
  );
}
